import React from 'react';
import PropTypes from 'prop-types';
import { LuCalendarRange, LuChevronDown, LuDownload } from 'react-icons/lu';
import Button from '../../../components/ui/Button';
import GlassIcon from '../../../components/ui/GlassIcon';
import { CARD } from '../../ProfilePage/components/ProfileUi';
import useSmoText from '../smoText';

const DATE_INPUT = 'tw-h-9 tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-2.5 tw-text-sm tw-tabular-nums tw-text-slate-800 focus:tw-border-teal-500 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-teal-500/30 dark:tw-border-white/10 dark:tw-bg-white/[0.04] dark:tw-text-slate-100 dark:[color-scheme:dark]';

/**
 * Date range of a report section. "Bu Ay" = from the 1st of this month to
 * today (the default); "Tümü" removes both limits.
 */
export const DateRangeBar = ({ id, start, end, onStart, onEnd, onThisMonth, onAllTime, allTitle }) => {
  const { label } = useSmoText();
  return (
    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2 tw-rounded-2xl tw-border tw-border-slate-200/80 tw-bg-slate-50/70 tw-px-3 tw-py-2 dark:tw-border-white/10 dark:tw-bg-white/[0.02]">
      <span className="tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs tw-font-semibold tw-text-slate-600 dark:tw-text-slate-300">
        <LuCalendarRange aria-hidden="true" className="tw-h-4 tw-w-4 tw-text-slate-400" />
        {label('dateRange.label')}
      </span>
      <input id={`${id}-start`} type="date" aria-label={label('dateRange.start')} className={DATE_INPUT} value={start} onChange={(e) => onStart(e.target.value)} />
      <span aria-hidden="true" className="tw-text-slate-400">—</span>
      <input id={`${id}-end`} type="date" aria-label={label('dateRange.end')} className={DATE_INPUT} value={end} onChange={(e) => onEnd(e.target.value)} />
      <Button size="sm" variant="ghost" onClick={onThisMonth} title={label('dateRange.thisMonthHint')}>{label('dateRange.thisMonth')}</Button>
      <Button size="sm" variant="ghost" onClick={onAllTime} title={allTitle || label('dateRange.allHint')}>{label('dateRange.all')}</Button>
      {!start && !end && <span className="tw-text-[11px] tw-font-medium tw-text-teal-700 dark:tw-text-teal-300">{label('dateRange.allActive')}</span>}
    </div>
  );
};

DateRangeBar.propTypes = {
  id: PropTypes.string.isRequired,
  start: PropTypes.string.isRequired,
  end: PropTypes.string.isRequired,
  onStart: PropTypes.func.isRequired,
  onEnd: PropTypes.func.isRequired,
  onThisMonth: PropTypes.func.isRequired,
  onAllTime: PropTypes.func.isRequired,
  allTitle: PropTypes.string,
};

/** Short status next to a section title ("7 vizit analiz edildi", "3 çalışan tamamlamadı"). */
export const SectionStatus = ({ tone = 'muted', children }) => {
  const tones = {
    muted: 'tw-text-slate-500 dark:tw-text-slate-400',
    alert: 'tw-rounded-full tw-bg-rose-500/10 tw-px-2.5 tw-py-0.5 tw-font-semibold tw-text-rose-700 dark:tw-text-rose-300',
  };
  return <span className={`tw-inline-flex tw-items-center tw-gap-1.5 tw-text-xs ${tones[tone]}`}>{children}</span>;
};

SectionStatus.propTypes = { tone: PropTypes.oneOf(['muted', 'alert']), children: PropTypes.node };

/**
 * One collapsible report section: title, a one-line explanation, the status
 * text, an optional Excel button and the show/hide toggle. Sections start
 * collapsed so their charts are not drawn until they are opened.
 */
const ReportSection = ({ icon: Icon, gradient, title, description, status, onExcel, expanded, onToggle, dateRange, children }) => {
  const { label } = useSmoText();
  return (
    <section className={`${CARD} tw-space-y-4 tw-p-4 sm:tw-p-5`}>
      <header className="tw-flex tw-flex-wrap tw-items-start tw-gap-3">
        <GlassIcon gradient={gradient} size="xs" className="tw-mt-0.5">
          <Icon aria-hidden="true" className="tw-h-[18px] tw-w-[18px]" />
        </GlassIcon>
        <div className="tw-min-w-0 tw-flex-1">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-1">
            <h2 className="tw-text-lg tw-font-semibold tw-tracking-tight tw-text-slate-900 dark:tw-text-white">{title}</h2>
            {status}
          </div>
          {description && <p className="tw-mt-0.5 tw-max-w-3xl tw-text-sm tw-leading-relaxed tw-text-slate-500 dark:tw-text-slate-400">{description}</p>}
        </div>
        <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-2">
          {onExcel && (
            <Button size="sm" variant="success" onClick={onExcel} title={label('section.excelHint')}>
              <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
              Excel
            </Button>
          )}
          <Button size="sm" onClick={onToggle} aria-expanded={expanded} title={expanded ? label('section.collapse') : label('section.expand')}>
            <LuChevronDown aria-hidden="true" className={`tw-h-4 tw-w-4 tw-transition-transform ${expanded ? 'tw-rotate-180' : ''}`} />
            <span className="max-sm:tw-sr-only">{expanded ? label('section.hide') : label('section.show')}</span>
          </Button>
        </div>
      </header>
      {dateRange}
      {children}
    </section>
  );
};

ReportSection.propTypes = {
  icon: PropTypes.elementType.isRequired,
  gradient: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  status: PropTypes.node,
  onExcel: PropTypes.func,
  expanded: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  dateRange: PropTypes.node,
  children: PropTypes.node,
};

export default ReportSection;
